<template>
  <DatePicker
    ref="datepicker"
    :type="type"
    :format="localeFormat"
    :disabled-date="disabledDate"
    :clearable="false"
    :input-class="[
      inputClass, 
      { 
        'schedule-input': isSchedule === true,
        'time-ampm': hasAMPM
      }
    ]"
    :class="isSchedule === true ? 'schedule-datepicker' : ''"
    :lang="lang"
    :minute-step="15"
    :open.sync="open"
    @change="change"
    :value-type="valueType"
    v-model="date"
    :popup-class="[
      'mx-content-time',
      { 
        'schedule-popup': isSchedule === true, 
        'schedule-popup-ptbr': isSchedule === true && lang === 'pt-br' 
      }
    ]"
  />
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';
import * as ptBR from 'vue2-datepicker/locale/pt-br';
import dayjs from '../../core/dayjs';

const lang = {
  ...ptBR,
  formatLocale: {
    ...ptBR.formatLocale,
    weekdaysMin: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    monthsShort: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ],
  },
};

DatePicker.locale('pt-br', lang);

export default {
  components: {
    DatePicker,
  },

  props: {
    value: {
      default: null,
    },
    inputClass: {
      type: String,
      default: 'form-control',
    },
    type: {
      type: String,
      default: 'date',
    },
    future: {
      type: Boolean,
      default: true,
    },
    isSchedule: {
      type: Boolean,
      default: false
    }
  },

  created() {
    this.change(this.date);
  },

  data() {
    return {
      date: this.value ? this.value : new Date(),
      lang: BulldeskSettings.locale === 'pt_BR' ? 'pt-br' : 'en',
      open: false,
    };
  },

  computed: {
    valueType() {
      if (typeof this.date === 'object') {
        return 'date';
      }

      return this.returnFormat;
    },

    /**
     * The locale format.
     */
    localeFormat() {
      if (this.type === 'time') {
        return dayjs().getFormat('LT');
      }

      if (this.type === 'date') {
        return dayjs().getFormat('LD');
      }

      return null;
    },

    /**
     * The format to return dates
     */
    returnFormat() {
      if (this.type === 'time') {
        return 'HH:mm';
      }

      if (this.type === 'date') {
        return 'YYYY-MM-DD';
      }

      if (this.type === 'datetime') {
        return 'YYYY-MM-DD HH:mm';
      }

      return null;
    },

    hasAMPM() {
      if (!this.date) return false;

      if (this.lang === 'en') {
        return true;
      }
      return false;
    },
  },

  methods: {
    disabledDate(date) {
      return this.future && date < new Date(new Date().setHours(0, 0, 0, 0));
    },

    change(value, type) {
      if (type === 'minute') {
        this.open = false;
      }

      let formatted;
      const format = this.returnFormat;

      if (format) {
        formatted = dayjs(value).format(format);
      }

      if (formatted.toString() !== 'Invalid Date') {
        value = formatted;
      }

      this.$emit('input', value);
    },
  },
};
</script>

<style lang="scss">
@import '../../../../node_modules/vue2-datepicker/scss/index.scss';

.mx-calendar,
.mx-table,
.mx-date-row {
  font-family: Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
}

.mx-datepicker {
  width: 100%;

  &.schedule-datepicker {
    width: auto;
    
    .mx-input-wrapper {
      position: relative;
      display: inline-flex;
      width: fit-content;
      flex-shrink: 0;
    }

    .mx-icon-calendar {
      display: block !important;
      position: absolute;
      left: 8px;
      color: #242424;
      font-size: 16px;
      z-index: 1;
    }

    input {
      padding: 12px 11px !important;
      padding-left: 32px !important;
      font-weight: 400 !important;
      height: auto;
      width: 82px;
      flex-shrink: 0;
    }
  }
}

.mx-calendar-header-label {
  .mx-btn.mx-btn-text.mx-btn-current-month,
  .mx-btn.mx-btn-text.mx-btn-current-year {
    color: #3b334a;
    font-weight: 700;
    letter-spacing: 0.26px;
  }
}

.mx-btn.mx-btn-text.mx-btn-icon-double-left,
.mx-btn.mx-btn-text.mx-btn-icon-left,
.mx-btn.mx-btn-text.mx-btn-icon-double-right,
.mx-btn.mx-btn-text.mx-btn-icon-right {
  color: #3b334a;
}

.mx-table.mx-table-date th {
  color: #3b334a;
  font-weight: 700;
  letter-spacing: 0.22px;
}

.mx-icon-calendar {
  display: none;
}

.mx-date-row {
  .cell {
    color: #3b334a;
  }

  .cell.active {
    background-color: #7123bd;
    border-radius: 15px;
  }

  .cell.disabled {
    background-color: #fff;
  }
}

.mx-time-column .mx-time-list::after {
  height: 5px;
}

/* Estilos específicos do Schedule */
.schedule-popup.mx-datepicker-popup,
.schedule-popup.mx-datepicker-main,
.schedule-popup.mx-content-time {
  border-radius: 8px !important;
  margin-top: 4px !important;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: center;
  color: #242424;
  height: 230px;
  min-width: 173px;
  width: auto;
  position: absolute !important;
  z-index: 1000 !important;
}

.schedule-popup .mx-time {
  border-radius: 8px !important;
  height: 230px;
  min-width: 173px;
  width: auto;
}

.schedule-popup {
  .mx-time-column .mx-scrollbar-wrap {
    margin-right: 0 !important;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;  /* IE e Edge */
    scrollbar-width: none;  /* Firefox */
  }
  .mx-time-column .mx-scrollbar-track {
    display: none;
  }
}

.schedule-popup .mx-time-column .mx-time-item:hover {
  color: #242424;
  background-color: #CBD5E1;
  height: 32px;
  transition: background-color 0.5s ease;
  font-size: 14px;
}

.schedule-popup-ptbr .mx-time-column .mx-time-item:hover {
  width: 56px !important;
  margin-left: 15px !important;
}

.schedule-popup .mx-time-column .mx-time-item.active {
  color: #AAAAAB;
  background-color: transparent;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  text-decoration-line: line-through;
}

.schedule-popup .mx-time-column .mx-time-item {
  font-size: 14px;
}
 
.schedule-popup .mx-time-column .mx-time-list {
  padding: 0px;
}

.schedule-input.form-control:focus,
.schedule-input.mx-input:focus,
.mx-datepicker .schedule-input.mx-input:focus,
.form-group .schedule-input.mx-input:focus,
.form-group .schedule-input:focus,
.schedule-input.form-control.time:focus {
  border-color: #D1D1D1 !important;
}

input.time-ampm {
  width: 100px !important;
}
</style>
